import { type FC } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface ErrorDisplayProps {
  error: Error;
  onReset?: () => void;
  showDetails?: boolean;
}

/**
 * User-friendly error display component
 * Shows appropriate error messages and recovery options
 */
export const ErrorDisplay: FC<ErrorDisplayProps> = ({
  error,
  onReset,
  showDetails = false,
}) => {
  const navigate = useNavigate();

  // Determine if error is recoverable and what actions to show
  const isNetworkError = error.message.toLowerCase().includes('network') ||
    error.message.toLowerCase().includes('fetch') ||
    error.message.toLowerCase().includes('connection');

  const isAuthError = error.message.toLowerCase().includes('auth') ||
    error.message.toLowerCase().includes('unauthorized') ||
    error.message.toLowerCase().includes('session');

  const isValidationError = error.message.toLowerCase().includes('validation') ||
    error.message.toLowerCase().includes('invalid');

  // Get user-friendly error message (don't expose internal details)
  const getUserFriendlyMessage = (): string => {
    if (isNetworkError) {
      return 'We\'re having trouble connecting to the server. Please check your internet connection and try again.';
    }

    if (isAuthError) {
      return 'Your session has expired. Please log in again to continue.';
    }

    if (isValidationError) {
      return 'Some of the information provided is invalid. Please review and try again.';
    }

    // Generic error message that doesn't expose internal details
    return 'Something went wrong. We\'ve been notified and are working to fix it.';
  };

  // Get recovery guidance
  const getRecoveryGuidance = (): string => {
    if (isNetworkError) {
      return 'Try refreshing the page or checking your internet connection.';
    }

    if (isAuthError) {
      return 'Please log in again to continue your registration.';
    }

    if (isValidationError) {
      return 'Please go back and review your selections.';
    }

    return 'You can try refreshing the page or return to the home page.';
  };

  const handleRetry = (): void => {
    if (onReset) {
      onReset();
    } else {
      window.location.reload();
    }
  };

  const handleGoHome = (): void => {
    navigate('/');
  };

  const handleLogin = (): void => {
    navigate('/login?redirect=' + encodeURIComponent(window.location.pathname));
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-turquoise-800 via-turquoise-700 to-turquoise-800 flex items-center justify-center p-4">
      <Card className="backdrop-blur-md bg-turquoise-800/90 border-2 border-orange-500 max-w-2xl w-full">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-orange-500/20 flex items-center justify-center">
              <AlertTriangle className="h-6 w-6 text-orange-400" />
            </div>
            <CardTitle className="font-viking text-2xl text-white">
              Oops! Something Went Wrong
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* User-friendly error message */}
          <div className="rounded-lg bg-orange-500/10 border border-orange-500/30 p-4">
            <p className="text-orange-100 font-medium">
              {getUserFriendlyMessage()}
            </p>
          </div>

          {/* Recovery guidance */}
          <div className="space-y-2">
            <p className="text-sm font-medium text-white">What you can do:</p>
            <p className="text-sm text-orange-200">
              {getRecoveryGuidance()}
            </p>
          </div>

          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            {isAuthError ? (
              <>
                <Button
                  onClick={handleLogin}
                  variant="secondary"
                  className="flex-1"
                >
                  Log In
                </Button>
                <Button
                  onClick={handleGoHome}
                  variant="outline"
                  className="flex-1 border-orange-500 text-orange-200 hover:bg-orange-500/20"
                >
                  <Home className="h-4 w-4 mr-2" />
                  Go Home
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={handleRetry}
                  variant="secondary"
                  className="flex-1"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Try Again
                </Button>
                <Button
                  onClick={handleGoHome}
                  variant="outline"
                  className="flex-1 border-orange-500 text-orange-200 hover:bg-orange-500/20"
                >
                  <Home className="h-4 w-4 mr-2" />
                  Go Home
                </Button>
              </>
            )}
          </div>

          {/* Technical details (only in development or if explicitly shown) */}
          {showDetails && process.env.NODE_ENV === 'development' && (
            <details className="mt-6">
              <summary className="text-sm text-orange-200 cursor-pointer hover:text-orange-100">
                Technical Details (Development Only)
              </summary>
              <div className="mt-2 p-3 bg-turquoise-900/50 rounded text-xs text-orange-200 font-mono overflow-auto">
                <p className="font-bold mb-2">Error: {error.name}</p>
                <p className="mb-2">Message: {error.message}</p>
                {error.stack && (
                  <pre className="whitespace-pre-wrap text-xs">
                    {error.stack}
                  </pre>
                )}
              </div>
            </details>
          )}

          {/* Support message */}
          <div className="pt-4 border-t border-turquoise-600">
            <p className="text-xs text-orange-200/80 text-center">
              If this problem persists, please contact support with the time this error occurred.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
